/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useMemo } from 'react';

import {
  ApolloClient,
  ApolloLink,
  ApolloProvider,
  defaultDataIdFromObject,
  HttpLink,
  InMemoryCache,
} from '@apollo/client';
import { useCapSignals } from '@hooks/use-cap-signals';
import { useStores } from '@hooks/use-stores';
import { type Config } from '@stores/config-store';
import TimeoutLink from 'apollo-link-timeout';
import { type PageData } from 'bernie-http/dist/page';
import { useDyffContext } from 'dyff-plugin/dist/context';
import fetch from 'isomorphic-fetch';
import { type CapErrorCategory, type CapFrustrationCategory } from 'src/constants';

import { contextHandlerLink } from './links/context-link';
import { errorLink } from './links/error-link';
import { forwardHeadersLink } from './links/forward-headers-link';
import { pageIdLink } from './links/page-id-link';
import { retryLink } from './links/retry-link';
import { rumHandler } from './links/rum-link';
import possibleTypes from './possible-types.json';
import policies from './type-policies';

type IGraphType = 'CONTROL-PLANE' | 'BEX' | 'EGA';

type IProps = React.PropsWithChildren<{
  graphType: IGraphType;
  ssrConfig?: Config;
  ssrRequest?: any;
}>;

export const GraphProvider: React.FC<IProps> = ({ graphType, ssrConfig, ssrRequest, children }) => {
  const isSSR = typeof window === 'undefined';

  const { page, config } = useStores();

  const { publishError, publishFrustration, publishAction } = useCapSignals();
  const { isFeatureEnabled } = useDyffContext();
  const useRum = isFeatureEnabled('GRAPHQL_RUM');
  const publishPerformanceStats = isFeatureEnabled('GRAPHQL_PERFORMANCE_STATS');

  const links = getLinks(
    graphType,
    page,
    config?.appConfig ?? ssrConfig,
    isSSR,
    ssrRequest,
    publishError,
    publishFrustration,
    publishAction,
    useRum,
    publishPerformanceStats,
  );

  let cache = useMemo(() => {
    return new InMemoryCache({
      dataIdFromObject(responseObject) {
        return defaultDataIdFromObject(responseObject);
      },
      typePolicies: policies,
      possibleTypes: possibleTypes,
    });
  }, []);

  if (!isSSR) {
    //@ts-ignore test
    cache = cache.restore(window.__APOLLO_STATE__);
  }

  const env = config?.appConfig?.deployedEnv ?? ssrConfig.deployedEnv;

  const client = useMemo(() => {
    return new ApolloClient({
      link: ApolloLink.from(links),
      ssrMode: isSSR,
      cache: cache,
      resolvers: {},
      connectToDevTools: env == 'dev',
      name: graphType,
    });
  }, [graphType]);

  return <ApolloProvider client={client}>{children}</ApolloProvider>;
};

const getGraphUrl = (graphType: IGraphType, config, isSSR: boolean) => {
  const { graphqlConfig, appUrl } = config;
  let url = '';

  switch (graphType) {
    case 'CONTROL-PLANE':
      url = graphqlConfig.controlPlaneGraph;
      break;
    case 'BEX':
      url = graphqlConfig.bexGraph;
      break;
    case 'EGA':
      url = graphqlConfig.egaGraph;
      break;
  }

  if (isSSR) {
    url = appUrl + url;
  }

  return url;
};

export const getLinks = (
  graphType: IGraphType,
  page: PageData,
  config: Config,
  isSSR: boolean,
  ssrRequest: any,
  publishError: (category: CapErrorCategory, error?: Error) => void,
  publishFrustration: (category: CapFrustrationCategory, error?: Error) => void,
  publishAction: (action: string, context: any) => void,
  useRum: boolean,
  publishPerformanceStats: boolean,
) => {
  const { graphqlConfig } = config;

  const links = [];

  const error = errorLink(publishError);
  links.push(error);

  if (isSSR) {
    const timeout = new TimeoutLink(graphqlConfig.serverTimeout);
    links.push(timeout);
    const headers = forwardHeadersLink(ssrRequest?.headers);
    links.push(headers);
  } else {
    const timeout = new TimeoutLink(graphqlConfig.browserTimeout);
    links.push(timeout);
    const retry = retryLink(publishError, graphqlConfig.retryStrategy);
    links.push(retry);
    const pageId = pageIdLink(page);
    links.push(pageId);
  }

  if (useRum) {
    const rum = rumHandler(publishFrustration, publishAction, publishPerformanceStats);
    links.push(rum);
  }

  const contextLink = contextHandlerLink();
  links.push(contextLink);

  const http = new HttpLink({
    uri: getGraphUrl(graphType, config, isSSR),
    headers: {
      'Client-Info': 'advertiser-portal-pwa',
      'x-page-id': (page && page.pageId) || null,
    },
    credentials: 'same-origin',
    fetch,
  });

  links.push(http);

  return links;
};
